import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import type { Distribution } from "@memorang/types";
import { t } from "i18next";
//@ts-ignore
import numeral from "numeral";

type MasteryLevel = AppConfigMasteryLevelsType[number];

export const getMasteryPercentage = (distribution: Distribution): number => {
	const { high = 0, low = 0, medium = 0, none = 0 } = distribution;
	const total = high + none + low + medium;
	if (total <= 0) {
		return 0;
	}
	return Math.round((high / total) * 100);
};

export const resolveMasteryLevel = (
	distribution: Distribution,
	masteryLevels?: AppConfigMasteryLevelsType,
): MasteryLevel | undefined => {
	if (!masteryLevels?.length) {
		return undefined;
	}
	const percentage = getMasteryPercentage(distribution);
	return resolveMasteryLevelForPercentage(percentage, masteryLevels);
};

export const resolveMasteryLevelForPercentage = (
	percentage: number,
	masteryLevels?: AppConfigMasteryLevelsType,
): MasteryLevel | undefined => {
	if (!masteryLevels?.length) {
		return undefined;
	}
	const clamped = Math.min(Math.max(percentage, 0), 100);
	const sorted = [...masteryLevels].sort(
		(a, b) => (a.range.score[0] ?? 0) - (b.range.score[0] ?? 0),
	);
	const match = sorted.find(
		(level) =>
			clamped >= (level.range.score[0] ?? 0) &&
			clamped <= (level.range.score[1] ?? 100),
	);
	if (match) {
		return match;
	}

	const bandsBelow = sorted.filter(
		(level) => clamped >= (level.range.score[0] ?? 0),
	);
	return bandsBelow.at(-1) ?? sorted[0];
};

export const resolveMasteryColorForPercentage = (
	percentage: number,
	masteryLevels: AppConfigMasteryLevelsType | undefined,
	isDark: boolean,
): string | undefined => {
	const level = resolveMasteryLevelForPercentage(percentage, masteryLevels);
	return level && (isDark ? level.darkColor : level.lightColor);
};

/** English-only pluralization: appends a literal "s". Safe only for hard-coded
 *  English strings. Never pass a translated noun — use `smartPluralize` for
 *  anything that goes through `t()`, or CJK locales render "アイテムs". */
export const formatCount = (text: string, count: number, reverse?: boolean) =>
	reverse
		? `${count} ${text}${count !== 1 ? "s" : ""}`
		: `${text}${count !== 1 ? "s" : ""}`;

export const formatNumber = (num: number): string =>
	num?.toLocaleString("en-US");

export const convertToMinutes = (seconds: number) => Math.ceil(seconds / 60);

export const formatSecondsToUTCtime = (seconds: number) => {
	const date = new Date(seconds * 1000);
	const hr = date.getUTCHours();
	const min = String(date.getUTCMinutes()).padStart(2, "0");
	const sec = String(date.getUTCSeconds()).padStart(2, "0");
	return hr ? `${hr}:${min}:${sec}` : `${min}:${sec}`;
};

export const formatRank = (rank: number) => {
	if (!rank) {
		return null;
	}
	switch (rank) {
		case 1:
			return `${rank}st`;
		case 2:
			return `${rank}nd`;
		case 3:
			return `${rank}rd`;
		default:
			return `${rank}`;
	}
};

export const titleCase = (s: string) => {
	const [c, ...rest] = s.split("");
	return `${c?.toUpperCase()}${rest.join("")}`;
};

export const formatNumberWithAbbreviation = (num: number): string => {
	return numeral(num).format("0.[0]a");
};

/** Localized noun for a count, e.g. `smartPluralize("item", 2)` -> "items".
 *  Relies on i18next plural resolution (`common.<noun>_one` / `_other`) so each
 *  locale controls its own plural rules. Never append plural suffixes in JS. */
export function smartPluralize(
	singular: string,
	count: number,
	options?: { language?: string },
) {
	return t(`common.${singular}`, { count, lng: options?.language });
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

/** Format seconds into a human-readable duration.
 *  Default: "2h 30m", "15m", "< 1m".
 *  With `includeSeconds: true`: "1m 23s", "45s" (sub-minute precision). */
export const formatSecondsToDurationLabel = (
	seconds: number,
	includeSeconds = false,
): string => {
	const hours = Math.floor(seconds / SECONDS_PER_HOUR);
	const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
	const secs = seconds % SECONDS_PER_MINUTE;
	if (hours > 0) {
		return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
	}
	if (minutes > 0) {
		return includeSeconds && secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
	}
	return includeSeconds ? `${secs}s` : "< 1m";
};

/** Format seconds into MM:SS or HH:MM:SS for playback display. */
export const formatSecondsToTimestamp = (
	seconds: number,
	{ padMinutes = false }: { padMinutes?: boolean } = {},
): string => {
	const totalSeconds = Math.max(0, Math.floor(seconds));
	const hrs = Math.floor(totalSeconds / SECONDS_PER_HOUR);
	const mins = Math.floor(
		(totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
	);
	const secs = totalSeconds % SECONDS_PER_MINUTE;
	const minsStr = padMinutes
		? mins.toString().padStart(2, "0")
		: mins.toString();
	if (hrs > 0) {
		return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	}
	return `${minsStr}:${secs.toString().padStart(2, "0")}`;
};

export const SAFE_SCHEMES = ["https", "mailto", "tel"] as const;

export const isSafeUrl = (
	url?: string,
	{
		allowedSchemes = SAFE_SCHEMES as readonly string[],
	}: { allowedSchemes?: readonly string[] } = {},
): boolean => {
	if (!url) {
		return false;
	}
	const schemeMatch = url.match(/^([a-z][a-z0-9+\-.]*):./i);
	if (!schemeMatch) {
		return false;
	}
	const scheme = schemeMatch[1].toLowerCase();
	return allowedSchemes.some((s) => s.toLowerCase() === scheme);
};

export const interpolateString = (
	template: string,
	values: Record<string, string | number>,
): string =>
	template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(values[key] ?? ""));

export const calculateAccuracy = ({
	numCorrect,
	numTotal,
}: {
	numCorrect: number;
	numTotal: number;
}) => (numTotal > 0 ? Math.round((numCorrect / numTotal) * 100) : 0);
